import type { OrderlineSelectionView } from 'shared-models/browser';

export type ComboSubItem = {
  itemId: number | null;
  name: string;
  quantity: number;
  extraPrice: number;
  nestedSelections: OrderlineSelectionView[];
  kds?: boolean | null;
  printerIds?: number[];
  areaPrintRuleId?: string | null;
};

export type ComboSelectionRoute = {
  kds?: boolean | null;
  printerIds?: number[];
  areaPrintRuleId?: string | null;
};

export type OrderlineSelectionSource = {
  productId?: number | null;
  selectionLabels?: string[];
  selections?: OrderlineSelectionView[];
  [key: string]: unknown;
};

export function splitOrderlineSelections(
  line: OrderlineSelectionSource,
  resolveComboSelectionRoute?: (productId: number, itemId: number) => ComboSelectionRoute | null,
): { variantLabels: string[]; comboItems: ComboSubItem[] } {
  if (line.selections && line.selections.length > 0) {
    const variantLabels: string[] = [];
    const comboItems: ComboSubItem[] = [];
    const nestedVariantsByParent = new Map<number, OrderlineSelectionView[]>();
    for (const selection of line.selections) {
      if (selection.groupType === 'variant' && selection.parentItemPk != null) {
        const nested = nestedVariantsByParent.get(selection.parentItemPk) ?? [];
        nested.push(selection);
        nestedVariantsByParent.set(selection.parentItemPk, nested);
      }
    }
    for (const selection of line.selections) {
      const label =
        selection.quantity > 1 ? `${selection.itemName} ×${selection.quantity}` : selection.itemName;
      if (selection.groupType === 'combo') {
        const route = line.productId != null && selection.itemId != null
          ? resolveComboSelectionRoute?.(line.productId, selection.itemId)
          : null;
        comboItems.push({
          itemId: selection.itemId,
          name: label,
          quantity: selection.quantity,
          extraPrice: selection.extraPrice ?? 0,
          nestedSelections: nestedVariantsByParent.get(selection.itemPk ?? selection.itemId ?? -1) ?? [],
          kds: selection.kds !== undefined ? selection.kds : route?.kds,
          printerIds: selection.printerIds ?? route?.printerIds ?? [],
          areaPrintRuleId: route?.areaPrintRuleId ?? null,
        });
      } else if (selection.parentItemPk == null) {
        variantLabels.push(label);
      }
    }
    return { variantLabels, comboItems };
  }
  return { variantLabels: line.selectionLabels ?? [], comboItems: [] };
}

export type OrderlineSelectionOption = {
  name: string;
  extraPrice: number;
  quantity: number;
};

export type OrderlineSelectionGroup = {
  groupName: string;
  options: OrderlineSelectionOption[];
};

type GroupableSelection = {
  itemName: string;
  groupName?: string | null;
  quantity: number;
  extraPrice?: number | null;
};

// Groups selections by groupName (in arrival/selectionSort order) into labeled sub-lines.
export function groupOrderlineSelections(
  selections: GroupableSelection[] | null | undefined,
): OrderlineSelectionGroup[] {
  if (!selections || selections.length === 0) {
    return [];
  }
  const groups: OrderlineSelectionGroup[] = [];
  const indexByName = new Map<string, number>();
  for (const selection of selections) {
    const groupName = selection.groupName ?? '';
    const name =
      selection.quantity > 1 ? `${selection.itemName} ×${selection.quantity}` : selection.itemName;
    const option: OrderlineSelectionOption = {
      name,
      extraPrice: selection.extraPrice ?? 0,
      quantity: selection.quantity,
    };
    const existing = indexByName.get(groupName);
    if (existing != null) {
      groups[existing].options.push(option);
    } else {
      indexByName.set(groupName, groups.length);
      groups.push({ groupName, options: [option] });
    }
  }
  return groups;
}

// Sums a group's extra price across its options, weighted by the orderline quantity.
export function groupOrderlineExtraTotal(
  group: OrderlineSelectionGroup,
  lineQuantity: number,
): number {
  const perUnit = group.options.reduce((sum, opt) => sum + opt.extraPrice * opt.quantity, 0);
  return perUnit * lineQuantity;
}
